/**
 * Who may do what with events, as the server checks it
 * (services/server src/events/events.service.ts `mayRun`), so the screens
 * offer exactly what will work:
 * - making one takes Create Events where it lives (the channel for a voice
 *   or stage event, the server for one somewhere else);
 * - editing, starting, ending or cancelling your own takes Create Events
 *   there, anyone's Manage Events;
 * - and what the channel asks: View Channel and Connect for voice; Manage
 *   Channels, Mute Members and Move Members for a stage.
 */
import {
  ChannelType,
  GuildScheduledEventEntityType as ET,
  Permissions,
  type Channel,
  type GuildScheduledEvent,
  type Snowflake,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';
import { basePermissions, channelPermissions } from '../store/selectors';

const NEEDS: Record<number, bigint> = {
  [ET.VOICE]: Permissions.VIEW_CHANNEL | Permissions.CONNECT,
  [ET.STAGE_INSTANCE]:
    Permissions.VIEW_CHANNEL |
    Permissions.MANAGE_CHANNELS |
    Permissions.MUTE_MEMBERS |
    Permissions.MOVE_MEMBERS,
  [ET.EXTERNAL]: 0n,
};

/** Your permissions where an event lives. */
function scope(d: DataState, guildId: Snowflake, channelId: Snowflake | null): bigint {
  if (!channelId) return basePermissions(d, guildId).permissions;
  const c = d.channels[channelId];
  return c ? channelPermissions(d, c).permissions : 0n;
}

/** Whether you may make an event of this kind here. */
export function mayCreateAt(
  d: DataState,
  guildId: Snowflake,
  entityType: number,
  channelId: Snowflake | null,
): boolean {
  const p = scope(d, guildId, channelId);
  const need = NEEDS[entityType] ?? 0n;
  return (p & Permissions.CREATE_EVENTS) !== 0n && (p & need) === need;
}

/** Whether you may edit, start, end or cancel this event. */
export function mayRunEvent(d: DataState, e: GuildScheduledEvent): boolean {
  const p = scope(d, e.guild_id, e.channel_id);
  const mine = !!d.me && e.creator_id === d.me.id;
  const allowed =
    (p & Permissions.MANAGE_EVENTS) !== 0n || (mine && (p & Permissions.CREATE_EVENTS) !== 0n);
  const need = NEEDS[e.entity_type] ?? 0n;
  return allowed && (p & need) === need;
}

export interface CreateTargets {
  external: boolean;
  voice: Channel[];
  stage: Channel[];
}

/** Where you may make events (the editor's choices), channels in sidebar order. */
export function createTargets(d: DataState, guildId: Snowflake | null): CreateTargets {
  if (!guildId) return { external: false, voice: [], stage: [] };
  const chans = Object.values(d.channels)
    .filter((c) => c.guild_id === guildId)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : 1));
  return {
    external: mayCreateAt(d, guildId, ET.EXTERNAL, null),
    voice: chans.filter(
      (c) => c.type === ChannelType.GUILD_VOICE && mayCreateAt(d, guildId, ET.VOICE, c.id),
    ),
    stage: chans.filter(
      (c) =>
        c.type === ChannelType.GUILD_STAGE_VOICE &&
        mayCreateAt(d, guildId, ET.STAGE_INSTANCE, c.id),
    ),
  };
}

/** Whether you may make any event at all ("Create event" shows). */
export function mayCreateAny(d: DataState, guildId: Snowflake | null): boolean {
  const t = createTargets(d, guildId);
  return t.external || t.voice.length > 0 || t.stage.length > 0;
}
